import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getUserSettings, updateUserSettings, UserSettings } from '../api/userSettingsApi';
import './Settings.css';

const Settings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<UserSettings>({
    text_color: '#ffffff',
    background_color: '#000000',
    background_opacity: 0.60,
    font_size: 64,
    text_position_y: 0.75
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user settings
  useEffect(() => {
    if (user?.id) {
      loadUserSettings();
    }
  }, [user?.id]);

  const loadUserSettings = async () => {
    if (!user?.id) return;
    
    try {
      const userSettings = await getUserSettings(user.id);
      setSettings(userSettings);
    } catch (error) {
      console.error('Failed to load user settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSettingsChange = (key: keyof UserSettings, value: string | number) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!user?.id) {
      setMessage({ type: 'error', text: 'Usuario no encontrado' });
      return;
    }

    try {
      const updatedSettings = await updateUserSettings(user.id, settings);
      setSettings(updatedSettings);
      setMessage({ type: 'success', text: 'Configuración de historias actualizada exitosamente' });
    } catch (error) {
      console.error('Error updating settings:', error);
      setMessage({ type: 'error', text: 'Error al actualizar la configuración' });
    }
  };

  if (isLoading) {
    return (
      <div className="settings-container">
        <div className="loading">Cargando configuración...</div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <h1>Configuración</h1>
      
      {/* Story Text Settings Section */}
      <div className="settings-section">
        <h2>Configuración de texto para historias</h2>
        <p className="settings-description">
          Personaliza la apariencia del texto que aparece sobre las imágenes de las historias.
        </p>
        
        {message && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="settings-form">
          <div className="form-row">
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="textColor">Color del texto</label>
                <div className="color-input-container">
                  <input
                    type="color"
                    id="textColor"
                    value={settings.text_color}
                    onChange={(e) => handleSettingsChange('text_color', e.target.value)}
                    className="color-input"
                  />
                  <input
                    type="text"
                    value={settings.text_color}
                    onChange={(e) => handleSettingsChange('text_color', e.target.value)}
                    className="color-text-input"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="backgroundColor">Color de fondo</label>
                <div className="color-input-container">
                  <input
                    type="color"
                    id="backgroundColor"
                    value={settings.background_color}
                    onChange={(e) => handleSettingsChange('background_color', e.target.value)}
                    className="color-input"
                  />
                  <input
                    type="text"
                    value={settings.background_color}
                    onChange={(e) => handleSettingsChange('background_color', e.target.value)}
                    className="color-text-input"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    placeholder="#000000"
                  />
                </div>
              </div>
            </div>
            
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="backgroundOpacity">
                  Transparencia del fondo: {Math.round(settings.background_opacity * 100)}%
                </label>
                <input
                  type="range"
                  id="backgroundOpacity"
                  min="0"
                  max="1"
                  step="0.01"
                  value={settings.background_opacity}
                  onChange={(e) => handleSettingsChange('background_opacity', parseFloat(e.target.value))}
                  className="slider"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="fontSize">Tamaño de fuente: {settings.font_size}px</label>
                <input
                  type="range"
                  id="fontSize"
                  min="12"
                  max="200"
                  step="1"
                  value={settings.font_size}
                  onChange={(e) => handleSettingsChange('font_size', parseInt(e.target.value))}
                  className="slider"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="textPosition">
                  Posición vertical: {Math.round((1 - settings.text_position_y) * 100)}% desde abajo
                </label>
                <input
                  type="range"
                  id="textPosition"
                  min="0.1"
                  max="0.9"
                  step="0.01"
                  value={settings.text_position_y}
                  onChange={(e) => handleSettingsChange('text_position_y', parseFloat(e.target.value))}
                  className="slider"
                />
                <div className="position-labels">
                  <span>Arriba</span>
                  <span>Abajo</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Preview Section */}
          <div className="settings-preview">
            <h3>Vista previa</h3>
            <div className="preview-container">
              <div 
                className="preview-text-overlay"
                style={{
                  backgroundColor: `${settings.background_color}${Math.round(settings.background_opacity * 255).toString(16).padStart(2, '0')}`,
                  color: settings.text_color,
                  fontSize: `${Math.min(settings.font_size * 0.5, 32)}px`,
                  position: 'absolute',
                  top: `${settings.text_position_y * 100}%`,
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }}
              >
                Texto de ejemplo
              </div>
            </div>
          </div>
          
          <button type="submit" className="submit-button">
            Guardar configuración
          </button>
        </form>
      </div>
    </div>
  );
};

export default Settings;